import Vue from "vue";
import { get, uniq } from "lodash";
import { paginationFilterSortInitialObject } from "./constants";

/**
 * Func to make types for async operations
 * @param prefix
 * @returns {{request: string, success: string, errors: string}}
 */
export const createTypes = prefix => ({
  request: `${prefix}_REQUEST`,
  success: `${prefix}_SUCCESS`,
  error: `${prefix}_ERROR`
});

/**
 * Get error handlers mutations
 * @param prefixes
 * @param errorField
 * @param loadingField
 */
export const errorMutations = (
  prefixes,
  loadingField = "isLoading",
  errorField = "errors"
) => {
  const out = {};
  for (let prefix of prefixes) {
    out[prefix] = function(state, { payload }) {
      Vue.set(state, errorField, payload);
      Vue.set(state, loadingField, false);
    };
  }

  return out;
};

/**
 * Get loading handlers to avoid duplications
 * @param prefixes
 * @param loadingField
 * @param status - true of false - disable or enable loading
 */
export const loadingMutations = (
  prefixes,
  loadingField = "isLoading",
  status = true
) => {
  const out = {};
  for (let prefix of prefixes) {
    out[prefix] = function(state) {
      Vue.set(state, loadingField, status);
    };
  }
  return out;
};

/**
 * Resetting `isLoading` and `errors` values.
 * Just shortcut
 * @param state
 * @param errorsField
 * @param loadingField
 */
export const resetErrorsAndLoading = (
  state,
  errorsField = "errors",
  loadingField = "isLoading"
) => {
  Vue.set(state, errorsField, null);
  Vue.set(state, loadingField, false);
};

/**
 * Search object in array for current `keyField`
 * @param state
 * @param arrayField
 * @param keyField
 * @param value
 * @param compareValue - value which need to be equals to `keyField` value.
 * @param pushToFirstIdx - boolean
 */
export const replaceOrSetArrayObjectsValue = (
  state,
  arrayField,
  keyField,
  compareValue,
  value,
  pushToFirstIdx = false
) => {
  const idx = state[arrayField].findIndex(
    field => field[keyField] === compareValue
  );
  if (idx !== -1) {
    // if template exists in list
    Vue.set(state[arrayField], idx, value);
  } else {
    if (pushToFirstIdx) {
      state[arrayField].unshift(value);
    } else {
      state[arrayField].push(value);
    }
  }
};

/**
 * Side-effect function to create tags from SelectList.
 * Commit changes to the server.
 * @param dispatch
 * @param rawTags
 * @param tagsList
 * @returns {Promise<Array>}
 */
export async function createNewTagsFromLocal(dispatch, rawTags, tagsList) {
  let tags = [];

  for (const _tag of uniq(rawTags)) {
    if (_tag.indexOf("new-") === -1) {
      tags = [...tags, _tag];
      continue;
    }

    const tagToAdd = tagsList.find(tag => tag.id === _tag);

    if (tagToAdd) {
      const created = await dispatch(
        "contacts/createTag",
        {
          payload: { name: tagToAdd.name }
        },
        { root: true }
      );

      tags = [...tags, created.id];
    }
  }

  return tags;
}

/**
 * Get value by given path
 * @param path
 * @returns {function(*=): (any | (any) | undefined)}
 */
export const getter = path => state => get(state, path);

/**
 * Set value by key
 * @param key
 * @param value
 * @returns {function(*=): *}
 */
export const setter = (key, value) => state => Vue.set(state, key, value);

/**
 * Getting value from another part of state (namespace)
 * @param otherGetter
 * @returns {function(*, *, *, *): *}
 */
export const proxy = otherGetter => (s, g, r, rootGetters) =>
  rootGetters[otherGetter];

/**
 * Return default value for different VFormField type
 */
export const getDefaultValueForVFFType = type => {
  switch (type) {
    case "customData":
      return {};

    case "checkbox":
      return false;

    case "tagsCreator":
    case "tags":
      return [];

    case "switcher":
      return "";

    default:
      return "";
  }
};

/**
 * Setting metadata to store module from API response
 * @param response
 * @param state
 * @param keysToUse
 */
export const metadataSetter = (response, state, keysToUse) => {
  if (!response.metadata) {
    return;
  }

  const allKeys = Object.keys(paginationFilterSortInitialObject);
  const metaKeys = keysToUse.filter(item => allKeys.includes(item))

  for (let key of metaKeys) {
    setter(key, response.metadata[key])(state);
  }
};
